import Fuse from 'fuse.js';
import type React from 'react';
import { type MouseEvent, useMemo, useRef, useState } from 'react';
import scrollIntoView from 'scroll-into-view';

import type { NodeEntity } from '@/components/CourseMap/coursemap.types';
import useOutsideClick from '@/hooks/useOutsideClick';
import type { CourseWithFullDetails } from '@/lib/types';
import { useAlignmentStore } from '@/stores/alignmentStore';

import { StyledAutoComplete, StyledWrapper } from './AlignmentSearch.Styles';

type AlignmentSearchProps = {
	isMobile?: boolean;
	dataTutorial?: string;
};

const AlignmentSearch = ({ isMobile = false, dataTutorial }: AlignmentSearchProps) => {
	const courseSearchRef = useRef<HTMLDivElement>(document.createElement('div'));
	const inputRef = useRef<HTMLInputElement>(document.createElement('input'));
	const { courseHashMapStore, prunedAlignmentStore } = useAlignmentStore();
	const [suggestionsArray, setSuggestionsArray] = useState<CourseWithFullDetails[]>();
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [userInput, setUserInput] = useState('');
	const { disciplines, divisions } = useAlignmentStore();

	const options = useMemo(
		() => ({
			useExtendedSearch: true,
			threshold: 0.0,
			findAllMatches: true,
			shouldSort: true,
			ignoreLocation: true,
			keys: [
				'course_name',
				'grade',
				'course_description',
				'course_discipline',
				'course_division',
				'course_extras',
				'course_prereq',
				'courses_focuses.focus.focuses_skills.skill.skill_text',
				'courses_outcomes.outcome.outcome_text',
				'courses_resources.resource.resource_author',
				'courses_resources.resource.resource_detail',
				'courses_resources.resource.resource_isbn',
				'courses_resources.resource.resource_title',
			],
		}),
		[]
	);

	const filteredAlignment = useMemo(() => {
		return prunedAlignmentStore.filter((c: CourseWithFullDetails | any) => {
			const courseDisciplineId = c?.courses_disciplines?.discipline?.id;
			const courseDivisionId = c?.courses_divisions?.division?.id;
			const disciplineSelection = disciplines.find((d) => d.id === courseDisciplineId);
			const divisionSelection = divisions.find((d) => d.id === courseDivisionId);

			return disciplineSelection?.isSelected && divisionSelection?.isSelected;
		});
	}, [prunedAlignmentStore, disciplines, divisions]);

	const fuse = useMemo(() => new Fuse(filteredAlignment, options), [filteredAlignment, options]);

	const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.persist();
		const searchValue = event.target.value;
		setUserInput(searchValue);
		const result = fuse.search(event.target.value) as Fuse.FuseResult<any>[];

		setSuggestionsArray(result.map((x) => x.item));
		if (searchValue !== '') {
			setShowSuggestions(true);
		}
		if (searchValue === '') {
			setShowSuggestions(false);
		}
	};

	function handleSelection(card: NodeEntity) {
		const selectedCard = document.querySelector(`.course-${card?.data.tracker}`);
		if (!selectedCard) return;
		const selectedCards = document.querySelectorAll('.selected-in-search');
		// biome-ignore lint/complexity/noForEach: <explanation>
		// biome-ignore lint/suspicious/useIterableCallbackReturn: <explanation>
		selectedCards.forEach((card) => card.classList.remove('selected-in-search'));

		const disciplineCheckbox = document.getElementById(
			`${card.data.discipline}-checkbox`
		) as HTMLInputElement;
		const divisionCheckbox = document.getElementById(
			`${card.data.division}-checkbox`
		) as HTMLInputElement;

		if (disciplineCheckbox && !disciplineCheckbox.checked) {
			disciplineCheckbox.click();
		}

		if (divisionCheckbox && !divisionCheckbox.checked) {
			divisionCheckbox.click();
		}

		const highlightCard = (cardElm: Element) => {
			cardElm.classList.add('selected-in-search');
			setTimeout(() => {
				cardElm.classList.remove('selected-in-search');
			}, 2500);
		};

		const scrollToCard = (cardElm: Element) => {
			scrollIntoView(cardElm as HTMLElement, {
				align: {
					topOffset: 0,
				},
			});
		};

		if (selectedCard) {
			scrollToCard(selectedCard);
			highlightCard(selectedCard);
			return;
		}

		setTimeout(() => {
			const courseCard = document.querySelector(`.course-${card.data.tracker}`);
			if (courseCard) {
				scrollToCard(courseCard);
				highlightCard(courseCard);
			}
		});
	}

	const handleOnClick = (e: React.MouseEvent<HTMLElement>, node: CourseWithFullDetails) => {
		setShowSuggestions(false);
		const selectedNode = courseHashMapStore.get(node.course_id)?.[0];

		if (selectedNode) {
			inputRef.current.value = node.course_name;
			setUserInput(inputRef.current.value);
			handleSelection(selectedNode);
		}
	};

	const handleOnKeyDown = (e: React.KeyboardEvent) => {
		const searchValue = (e.target as HTMLInputElement).value;
		if (e.code === 'Enter') {
			const currentCourse = suggestionsArray?.find((c) => c.course_name === searchValue);

			if (!currentCourse && suggestionsArray?.length !== 0) {
				const firstCourse = suggestionsArray?.[0];
				if (firstCourse?.course_id) {
					const selectedNode = courseHashMapStore.get(firstCourse.course_id)?.[0];

					if (selectedNode) {
						handleSelection(selectedNode);
						inputRef.current.value = firstCourse.course_name ?? '';
					}
				}
				setShowSuggestions(false);
			}
			if (currentCourse?.course_id) {
				const selectedNode = courseHashMapStore.get(currentCourse.course_id)?.[0];

				if (selectedNode) {
					handleSelection(selectedNode);
					inputRef.current.value = currentCourse.course_name ?? '';
				}
				setShowSuggestions(false);
			}
		}
	};

	function clearSelect() {
		inputRef.current.value = '';
		setShowSuggestions(false);
		setUserInput('');
	}

	useOutsideClick(courseSearchRef, () => {
		setShowSuggestions(false);
	});

	return (
		<StyledWrapper
			data-tutorial={dataTutorial ?? 'search'}
			className="search--intro"
			$isMobile={isMobile}
		>
			<StyledAutoComplete ref={courseSearchRef}>
				<input
					placeholder="Search by course name or keywords"
					type="text"
					className="bg-white"
					onChange={handleOnChange}
					onKeyDown={handleOnKeyDown}
					ref={inputRef}
				/>
				{userInput !== '' && (
					<button type="button" onClick={clearSelect} className="clear-btn">
						<svg
							aria-hidden="true"
							width="8"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="currentColor"
						>
							<path d="M23.954 21.03l-9.184-9.095 9.092-9.174L21.03-.046l-9.09 9.179L2.764.045l-2.81 2.81L9.14 11.96.045 21.144l2.81 2.81 9.112-9.192 9.18 9.1z" />
						</svg>
						<span className="screen-reader-text">Clear Dropdown</span>
					</button>
				)}
				{showSuggestions && (
					<ul className="suggestions">
						{suggestionsArray?.map((suggestion: CourseWithFullDetails) => (
							<li
								aria-label={suggestion.course_name}
								aria-hidden
								key={`suggestion-${suggestion.course_id}`}
								onClick={(e: MouseEvent<HTMLElement>) => handleOnClick(e, suggestion)}
							>
								{suggestion.course_name}
							</li>
						))}
						{suggestionsArray?.length === 0 && (
							<li>
								<span>No matching courses found within the selected options.</span>
							</li>
						)}
					</ul>
				)}
			</StyledAutoComplete>
		</StyledWrapper>
	);
};
export default AlignmentSearch;
